/**
 * This file includes utility functions that can extract user data from the idToken generated after authentication.
 */
import { Claims, AvpContextClaims } from '@org-avp/context-claims';
import { getStorageItem } from '../ui/storage';
import { getCountryISO2 } from '../isoConverter';
import { memoize } from 'utils/functions';
import { IUserInfo, User } from './user';
import { IMarket, IBrand, brandSchema } from 'data/schema';

const getIdTokenPayload = memoize((idToken: string): Claims | null => {
  try {
    if (!idToken) return null;
    const tokenBody = atob(idToken.split('.')[1]);
    return JSON.parse(tokenBody);
  } catch (err) {
    console.error(`Failed to retrieve token:\n${err}`);
    return null;
  }
});

const DEALER_ID_REGEX = /^[A-Z]{3}[A-Z0-9]{5}([A-Z])$/;
const VALID_BRANDS = new Set<string>(brandSchema.options.filter((b) => b !== '*'));

export const getFilteredDealerIds = (claims?: Claims): string[] => {
  if (!claims) return [];
  const dealerIds = AvpContextClaims.getDealerIds(claims);
  return dealerIds.filter((id) => {
    const match = DEALER_ID_REGEX.exec(id);
    return match !== null && VALID_BRANDS.has(match[1] as IBrand);
  });
};

const extractAvpUserData = memoize((claims: Claims): IUserInfo => {
  const hasClaims = claims && Object.keys(claims).length;
  const userType = hasClaims ? 'dealer' : 'customer';
  const allBrands = claims ? AvpContextClaims.getBrands(claims) : [];
  const brand = allBrands[0] || (claims ? JSON.parse(claims['custom:brands'] ?? '[]')[0] : undefined) || 'A';
  const market =
    (claims && AvpContextClaims.getMarkets(claims)[0]) || JSON.parse(claims?.['custom:markets'] ?? '[]')?.[0];
  const iso2 = getCountryISO2(market) || 'de';
  const iso3 = (market ?? 'DEU') as IMarket;
  const groups = claims ? AvpContextClaims.getGroups(claims) : [];
  const email = `${claims?.email ?? ''}`;
  const name = claims ? `${claims['custom:firstname'] ?? ''} ${claims['custom:lastname'] ?? ''}`.trim() : '';
  const phoneNumber = `${claims?.['custom:phone'] ?? ''}`;
  const publicPicture = `${claims?.['custom:publicPicture'] ?? ''}`;
  const dealerIdKvps = getFilteredDealerIds(claims)[0] ?? '';
  const userPseudonym = claims ? (AvpContextClaims.getUserPseudonym(claims) ?? '') : '';
  const dealerIdsList: string[] = getFilteredDealerIds(claims);
  const markets: string[] = claims
    ? AvpContextClaims.getMarkets(claims).length
      ? AvpContextClaims.getMarkets(claims)
      : JSON.parse(claims['custom:markets'] ?? '[]')
    : [];

  return {
    type: userType,
    brand,
    brands: allBrands,
    market: { iso2, iso3 },
    groups,
    email,
    name,
    phoneNumber,
    publicPicture,
    dealerIdKvps,
    userPseudonym,
    dealerIdsList,
    markets,
  };
});

export function initAvpAuthentication() {
  User.config.getUserInfoCallback = (idToken) => {
    const claims = idToken ? getIdTokenPayload(idToken) : null;
    if (!claims) {
      // No claims means user is not logged in
      return null;
    }
    return extractAvpUserData(claims);
  };

  User.config.getAccessTokenCallback = () => {
    return getStorageItem('sessionStorage', 'auth:token')?.accessToken ?? '';
  };

  User.config.getIdTokenCallback = () => {
    return getStorageItem('sessionStorage', 'auth:token')?.idToken ?? '';
  };
}
